import type { FC, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { Dropdown } from '../../../../components/Dropdown';
import { StyledDropdownOptionGroup } from '../../../../components/Dropdown/styles';
import { Modal } from '../../../../components/Modal';
import type { Opportunity } from '../../../../domain/opportunity';
import { useCompetitorsQuery } from '../../../../features/competitors/useCompetitorsQuery';
import { useSearchCompetitorsQuery } from '../../../../features/competitors/useSearchCompetitorsQuery';
import { useUpdateCompetitorsMutation } from '../../../../features/competitors/useUpdateCompetitorsMutation';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { arraysAreEqual } from '../../../../services/array';
import { getFaviconUrl } from '../../../../services/favicon';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

import {
  DropdownButtonTooltip,
  StyledCompetitorDomain,
  StyledCompetitorFavicon,
  StyledCompetitorItem,
  StyledCompetitorOption,
  StyledCompetitorsCount,
  StyledCompetitorsDropdown,
  StyledCompetitorsDropdownButton,
  StyledCompetitorsDropdownWrapper,
  StyledCompetitorsList,
  StyledOpenWebsiteButton,
  StyledRemoveCompetitorButton,
} from './styles';

export type CompetitorsModalProps = {
  isOpen: boolean;
  onClose(): void;
  opportunity: Opportunity;
};

const MAX_COMPETITORS = 5;

export const CompetitorsModal: FC<CompetitorsModalProps> = ({ isOpen, onClose, opportunity }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const searchCompetitorsQuery = useSearchCompetitorsQuery(debouncedSearchTerm);
  const updateCompetitorsMutation = useUpdateCompetitorsMutation(opportunity, onClose);
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const competitorsQuery = useCompetitorsQuery();
  const competitors = useMemo(() => competitorsQuery.data ?? [], [competitorsQuery.data]);
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([]);
  const notSelectedCompetitors = useMemo(
    () => competitors.filter((c) => !selectedCompetitors.includes(c.domain)),
    [competitors, selectedCompetitors]
  );

  const isMaxCompetitorsSelected = selectedCompetitors.length >= MAX_COMPETITORS;

  const isDirty = useMemo(() => {
    const initiallySelectedCompetitors = competitors.filter((c) => c.isSelected).map(({ domain }) => domain);
    return !arraysAreEqual(selectedCompetitors, initiallySelectedCompetitors);
  }, [selectedCompetitors, competitorsQuery.data]);

  const searchResults = useMemo(() => {
    const results = searchCompetitorsQuery.data ?? [];
    return results.filter(({ name }) => !selectedCompetitors.includes(name));
  }, [searchCompetitorsQuery.data, selectedCompetitors]);

  useEffect(() => {
    setSelectedCompetitors(competitors.filter((c) => c.isSelected).map(({ domain }) => domain));
  }, [competitors]);

  const handleCloseFinish = () => {
    setSelectedCompetitors(competitors.filter((c) => c.isSelected).map(({ domain }) => domain));
  };

  const handleOpenWebsite = (domain: string) => {
    window.open(`https://${domain}`, '_blank');
  };

  const handleRemoveCompetitor = (domain: string) => {
    const updatedCompetitors = selectedCompetitors.filter((d) => d !== domain);
    dispatchTrackEventWithLocation('competitors', 'remove competitor', `${domain}/${updatedCompetitors.length}`);
    setSelectedCompetitors(updatedCompetitors);
  };

  const handleSelectCompetitor = (e: MouseEvent, domain: string) => {
    const updatedCompetitors = selectedCompetitors.concat(domain);
    e.stopPropagation();
    setSelectedCompetitors(updatedCompetitors);
    setIsDropdownOpen(false);
    dispatchTrackEventWithLocation('competitors', 'add competitor', `${domain}/${updatedCompetitors.length}`);
  };

  const handleApplyChanges = () => {
    updateCompetitorsMutation.mutate(selectedCompetitors);
    dispatchTrackEventWithLocation('competitors', 'apply changes', `change competitors/${selectedCompetitors.length}`);
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      confirmButtonProps={{
        children: t('common.apply'),
        onClick: handleApplyChanges,
        disabled: !isDirty || selectedCompetitors.length === 0 || updateCompetitorsMutation.isPending,
        loading: updateCompetitorsMutation.isPending,
      }}
      cancelButtonProps={{ children: t('common.cancel'), onClick: onClose, variant: 'flat' }}
      heading="Competitors"
      onCloseFinish={handleCloseFinish}
      contentHorizontalPadding={0}
    >
      <StyledCompetitorsDropdownWrapper>
        <StyledCompetitorsDropdown>
          <DropdownButtonTooltip
            enabled={isMaxCompetitorsSelected}
            content={t('insights.competitors.disabled.tooltip', { maxCompetitors: MAX_COMPETITORS })}
            minWidth={230}
          >
            <StyledCompetitorsDropdownButton
              onClick={() => setIsDropdownOpen(true)}
              disabled={isMaxCompetitorsSelected}
            >
              <SWReactIcons iconName="add" size="sm" />
              {t('insights.competitors.search')}
            </StyledCompetitorsDropdownButton>
          </DropdownButtonTooltip>
          <Dropdown
            onCloseFinish={() => setSearchTerm('')}
            isOpened={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            minWidth={315}
            maxHeight={330}
            placement="on-top-left"
            searchProps={{
              value: searchTerm,
              onChange: (e) => setSearchTerm(e.target.value),
              placeholder: t('insights.competitors.search') ?? '',
              isLoading: searchCompetitorsQuery.isLoading,
              iconName: 'add',
            }}
          >
            {searchTerm ? (
              searchResults.map(({ name }) => (
                <StyledCompetitorOption key={name} onClick={(e) => handleSelectCompetitor(e, name)}>
                  <StyledCompetitorFavicon src={getFaviconUrl(name)} />
                  <StyledCompetitorDomain>{name}</StyledCompetitorDomain>
                </StyledCompetitorOption>
              ))
            ) : (
              <>
                <StyledDropdownOptionGroup>{t('insights.competitors.other_similar')}</StyledDropdownOptionGroup>
                {notSelectedCompetitors.map(({ domain }) => (
                  <StyledCompetitorOption key={domain} onClick={(e) => handleSelectCompetitor(e, domain)}>
                    <StyledCompetitorFavicon src={getFaviconUrl(domain)} />
                    <StyledCompetitorDomain>{domain}</StyledCompetitorDomain>
                  </StyledCompetitorOption>
                ))}
              </>
            )}
          </Dropdown>
        </StyledCompetitorsDropdown>
        <StyledCompetitorsCount data-automation="competitors-count">
          ({selectedCompetitors.length}/{MAX_COMPETITORS})
        </StyledCompetitorsCount>
      </StyledCompetitorsDropdownWrapper>
      <StyledCompetitorsList>
        {competitorsQuery.isLoading ? <div>Loading...</div> : null}
        {selectedCompetitors.map((domain) => (
          <StyledCompetitorItem key={domain} data-automation="competitors-item">
            <StyledCompetitorFavicon src={getFaviconUrl(domain)} />
            <StyledCompetitorDomain>{domain}</StyledCompetitorDomain>
            <StyledOpenWebsiteButton variant="flat" onClick={() => handleOpenWebsite(domain)}>
              <SWReactIcons iconName="open-new-window" size="xs" />
            </StyledOpenWebsiteButton>

            <StyledRemoveCompetitorButton variant="flat" onClick={() => handleRemoveCompetitor(domain)}>
              <SWReactIcons iconName="delete" size="xs" />
            </StyledRemoveCompetitorButton>
          </StyledCompetitorItem>
        ))}
      </StyledCompetitorsList>
    </Modal>
  );
};
